import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield, ShieldOff, Smartphone, Trash2, Search, RefreshCw, AlertTriangle, CheckCircle2,
  XCircle, Loader2, User, Key,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface UserRow {
  id: string;
  full_name: string | null;
  email: string | null;
  system_role: string;
  activeDevices: number;
}

interface MFAFactor {
  id: string;
  friendly_name: string;
  factor_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface TrustedDevice {
  id: string;
  device_name: string;
  created_at: string;
  expires_at: string;
  last_used_at: string;
  revoked_at: string | null;
}

const callAdminMFA = async (body: object) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await supabase.functions.invoke('admin-mfa-manage', { body });
  if (res.error) throw new Error(res.error.message);
  return res.data;
};

export function MFAAdminManager() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [search, setSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [factors, setFactors] = useState<MFAFactor[]>([]);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { toast } = useToast();

  // ── Charger les utilisateurs ─────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, system_role')
        .order('full_name', { ascending: true });

      if (!profiles) return;

      // Compter les appareils actifs par user
      const ids = profiles.map((p) => p.id);
      const result = await callAdminMFA({ action: 'GET_USERS_MFA_STATUS', userIds: ids });

      const rows: UserRow[] = profiles.map((p) => ({
        id: p.id,
        full_name: p.full_name,
        email: p.email,
        system_role: (p as any).system_role ?? 'client',
        activeDevices: result?.deviceCounts?.[p.id] ?? 0,
      }));
      setUsers(rows);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Ouvrir le détail d'un user ────────────────────────────────────────────
  const openUserModal = async (user: UserRow) => {
    setSelectedUser(user);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const [factorsRes, devicesRes] = await Promise.all([
        callAdminMFA({ action: 'GET_USER_FACTORS', userId: user.id }),
        callAdminMFA({ action: 'GET_USER_DEVICES', userId: user.id }),
      ]);
      setFactors(factorsRes?.factors ?? []);
      setDevices(devicesRes?.devices ?? []);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Supprimer un facteur TOTP ─────────────────────────────────────────────
  const deleteFactor = async (factorId: string) => {
    if (!selectedUser) return;
    setActionLoading(`factor-${factorId}`);
    try {
      await callAdminMFA({ action: 'DELETE_FACTOR', userId: selectedUser.id, factorId });
      setFactors((prev) => prev.filter((f) => f.id !== factorId));
      toast({ title: 'Facteur supprimé', description: 'L\'utilisateur devra re-configurer son MFA.' });
      loadUsers();
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Révoquer tous les appareils ───────────────────────────────────────────
  const revokeAllDevices = async () => {
    if (!selectedUser) return;
    setActionLoading('revoke-devices');
    try {
      await callAdminMFA({ action: 'REVOKE_USER_DEVICES', userId: selectedUser.id });
      setDevices([]);
      setUsers((prev) =>
        prev.map((u) => u.id === selectedUser.id ? { ...u, activeDevices: 0 } : u)
      );
      toast({ title: 'Appareils révoqués', description: 'Le MFA sera demandé à la prochaine connexion.' });
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Révoquer un appareil spécifique ──────────────────────────────────────
  const revokeDevice = async (deviceId: string) => {
    setActionLoading(`device-${deviceId}`);
    try {
      await callAdminMFA({ action: 'REVOKE_DEVICE', deviceId });
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
      setUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser?.id ? { ...u, activeDevices: Math.max(0, u.activeDevices - 1) } : u
        )
      );
      toast({ title: 'Appareil révoqué' });
    } catch (e: any) {
      toast({ title: 'Erreur', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = users.filter((u) =>
    !search ||
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const roleColor: Record<string, string> = {
    super_admin: 'bg-red-100 text-red-700',
    admin: 'bg-orange-100 text-orange-700',
    vendor: 'bg-blue-100 text-blue-700',
    hr: 'bg-purple-100 text-purple-700',
    relay: 'bg-teal-100 text-teal-700',
    client: 'bg-gray-100 text-gray-600',
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Gestion MFA par utilisateur
              </CardTitle>
              <CardDescription>
                Révoquer les facteurs TOTP, les appareils mémorisés ou forcer le re-enrollment.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadUsers} disabled={loadingUsers}>
              <RefreshCw className={`h-4 w-4 ${loadingUsers ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un utilisateur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-3 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{u.full_name || '—'}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor[u.system_role] || roleColor.client}`}>
                      {u.system_role}
                    </span>
                    {u.activeDevices > 0 ? (
                      <Badge variant="outline" className="gap-1 text-xs">
                        <Smartphone className="h-3 w-3" />
                        {u.activeDevices}
                      </Badge>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => openUserModal(u)}>
                      Gérer
                    </Button>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Aucun utilisateur trouvé</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Modal détail utilisateur ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              MFA — {selectedUser?.full_name || selectedUser?.email}
            </DialogTitle>
            <DialogDescription>
              Gérez les facteurs d'authentification et les appareils de confiance.
            </DialogDescription>
          </DialogHeader>

          {modalLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Facteurs TOTP */}
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Key className="h-4 w-4" /> Facteurs TOTP
                </h4>
                {factors.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                    <XCircle className="h-4 w-4 text-amber-500" />
                    Aucun facteur TOTP enregistré
                  </div>
                ) : (
                  <div className="space-y-2">
                    {factors.map((f) => (
                      <div key={f.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-sm font-medium">{f.friendly_name || 'Authenticator App'}</p>
                            <p className="text-xs text-muted-foreground">
                              Ajouté le {new Date(f.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={actionLoading === `factor-${f.id}`}
                          onClick={() => deleteFactor(f.id)}
                        >
                          {actionLoading === `factor-${f.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ShieldOff className="h-4 w-4 mr-1" />
                          )}
                          Révoquer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Appareils de confiance */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    <Smartphone className="h-4 w-4" /> Appareils mémorisés
                  </h4>
                  {devices.length > 0 && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={actionLoading === 'revoke-devices'}
                      onClick={revokeAllDevices}
                    >
                      {actionLoading === 'revoke-devices' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-1" />
                      )}
                      Tout révoquer
                    </Button>
                  )}
                </div>
                {devices.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                    Aucun appareil mémorisé
                  </p>
                ) : (
                  <div className="space-y-2">
                    {devices.map((d) => (
                      <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{d.device_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Dernière utilisation: {new Date(d.last_used_at).toLocaleDateString('fr-FR')}
                            {' · '}Expire: {new Date(d.expires_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={actionLoading === `device-${d.id}`}
                          onClick={() => revokeDevice(d.id)}
                        >
                          {actionLoading === `device-${d.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Warning */}
              {factors.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  Révoquer le facteur TOTP forcera l'utilisateur à reconfigurer son application d'authentification.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
